import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
  Plus, Receipt, ExternalLink, UserPlus, Check, X,
  Share2, Flag, FileText, Download, ArrowLeft,
  Users, Calendar, MapPin, Wallet, ChevronRight,
  Megaphone, Lock, Unlock, LayoutDashboard, Pencil
} from "lucide-react";

export default function ManageEventDashboard() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [event, setEvent] = useState(null);
  const [inviteUsername, setInviteUsername] = useState("");
  const [inviting, setInviting] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [userSuggestions, setUserSuggestions] = useState([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [downloadingSheet, setDownloadingSheet] = useState(false);
  const [downloadingReport, setDownloadingReport] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/events/${id}`);
      setEvent(data);
    } catch { toast.error("Failed to load event"); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Poll every 10 seconds
  useEffect(() => {
    const interval = setInterval(load, 10000);
    return () => clearInterval(interval);
  // eslint-disable-next-line
  }, [id]);

  const pendingInvite = event?.pending_invites?.includes(user?.id);
  const isOwner = event?.posted_by === user?.id;
  const isCoManager = event?.co_managers?.includes(user?.id);
  const isCompleted = event?.status === "completed";

  const acceptInvite = async () => {
    await api.post(`/events/${id}/accept`);
    toast.success("You are now a co-manager!");
    load();
  };

  const declineInvite = async () => {
    await api.post(`/events/${id}/decline`);
    toast.success("Invitation declined");
    nav("/dashboard");
  };

  const sendInvite = async () => {
    if (!inviteUsername.trim()) return;
    setInviting(true);
    try {
      const { data } = await api.post(`/events/${id}/invite`, { username: inviteUsername.trim() });
      toast.success(data.message);
      setInviteUsername(""); setShowInvite(false); setUserSuggestions([]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send invite");
    } finally { setInviting(false); }
  };

  const handleUsernameChange = async (val) => {
    setInviteUsername(val.toLowerCase().replace(/[^a-z0-9._]/g, ""));
    setUserSuggestions([]);
    if (val.length < 2) return;
    setSearchingUsers(true);
    try {
      const { data } = await api.get(`/users/search?q=${val}&limit=5`);
      setUserSuggestions(data);
    } catch {} finally { setSearchingUsers(false); }
  };

  const selectUser = (username) => {
    setInviteUsername(username);
    setUserSuggestions([]);
  };

  const markComplete = async () => {
    if (!window.confirm("Mark this event as completed? This will finalize points for all workers.")) return;
    try {
      await api.patch(`/events/${id}`, { status: "completed" });
      for (const job of event.jobs || []) {
        await api.patch(`/jobs/${job.id}`, { status: "completed" });
      }
      toast.success("Event marked as completed! Worker points updated.");
      load();
    } catch { toast.error("Failed to mark event as completed"); }
  };

  const reopenEvent = async () => {
    try {
      await api.patch(`/events/${id}`, { status: "active" });
      toast.success("Event reopened!");
      load();
    } catch { toast.error("Failed to reopen event"); }
  };

  const shareLink = () => {
    const url = `${window.location.origin}/events/${id}`;
    navigator.clipboard.writeText(url);
    toast.success("Event link copied!");
  };

  const downloadCallSheet = async () => {
    setDownloadingSheet(true);
    toast.info("Preparing call sheet…");
    try {
      const token = localStorage.getItem("em_token");
      const backendUrl = process.env.REACT_APP_BACKEND_URL || "https://earnmate-ij7g.onrender.com";
      const res = await fetch(`${backendUrl}/api/events/${id}/call-sheet`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error("Failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `call_sheet_${event?.name?.replace(/\s+/g, "_") || "event"}.pdf`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
      toast.success("Call sheet downloaded! ✓");
    } catch { toast.error("Failed to generate call sheet."); }
    finally { setDownloadingSheet(false); }
  };

  const downloadPostEventReport = async () => {
    setDownloadingReport(true);
    toast.info("Preparing post-event report…");
    try {
      const token = localStorage.getItem("em_token");
      const backendUrl = process.env.REACT_APP_BACKEND_URL || "https://earnmate-ij7g.onrender.com";
      const res = await fetch(`${backendUrl}/api/events/${id}/post-event-report`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error("Failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `post_event_report_${event?.name?.replace(/\s+/g, "_") || "event"}.pdf`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
      toast.success("Post-event report downloaded! ✓");
    } catch { toast.error("Failed to generate report."); }
    finally { setDownloadingReport(false); }
  };

  if (!event) return <div className="p-12 text-center text-muted-foreground">Loading…</div>;

  const totalSlots = event.jobs?.reduce((s, j) => s + (j.total_slots || 0), 0) || 0;
  const confirmedSlots = event.jobs?.reduce((s, j) => s + (j.confirmed_count || 0), 0) || 0;
  const presentCount = event.jobs?.reduce((s, j) => s + (j.present_count || 0), 0) || 0;

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-8">
      {/* Back to dashboard */}
      <Link to="/dashboard" className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-brand mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to dashboard
      </Link>

      {/* Pending invite banner */}
      {pendingInvite && (
        <div className="mb-6 card-flat border-brand/40 bg-brand-tint/30 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="font-medium text-foreground">You've been invited to co-manage this event</p>
            <p className="text-sm text-muted-foreground">"{event.name}"</p>
          </div>
          <div className="flex gap-2">
            <button onClick={declineInvite} className="btn-outline !py-2 !px-4 text-sm flex items-center gap-1"><X className="w-4 h-4" /> Decline</button>
            <button onClick={acceptInvite} className="btn-primary !py-2 !px-4 text-sm flex items-center gap-1"><Check className="w-4 h-4" /> Accept</button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="label-eyebrow text-muted-foreground">Event dashboard</span>
            {isCompleted && (
              <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700 border border-emerald-200">
                ✓ Completed
              </span>
            )}
          </div>
          <h1 className="mt-1 font-display text-3xl tracking-tighter text-foreground">{event.name}</h1>
          <div className="mt-2 flex flex-wrap gap-3 text-sm text-muted-foreground">
            {event.date && (
              <span className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4" />
                {new Date(event.date).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
              </span>
            )}
            {event.venue && (
              <span className="flex items-center gap-1.5">
                <MapPin className="w-4 h-4" />
                {event.venue}
              </span>
            )}
            {event.budget && (
              <span className="flex items-center gap-1.5">
                <Wallet className="w-4 h-4" />
                Budget: ₹{event.budget.toLocaleString("en-IN")}
              </span>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex gap-2 flex-wrap">
          <button onClick={shareLink} className="btn-outline text-sm flex items-center gap-1.5">
            <Share2 className="w-4 h-4" /> Share
          </button>
          <button onClick={downloadCallSheet} disabled={downloadingSheet} className="btn-outline text-sm flex items-center gap-1.5">
            <Download className={`w-4 h-4 ${downloadingSheet ? "animate-spin" : ""}`} />
            {downloadingSheet ? "Generating…" : "Call sheet"}
          </button>
          {isCompleted && (
            <button onClick={downloadPostEventReport} disabled={downloadingReport} className="btn-outline text-sm flex items-center gap-1.5 !border-emerald-500 !text-emerald-600">
              <FileText className={`w-4 h-4 ${downloadingReport ? "animate-spin" : ""}`} />
              {downloadingReport ? "Generating…" : "Post-event report"}
            </button>
          )}
          {!isCompleted && isOwner && (
            <button onClick={markComplete} className="btn-outline text-sm flex items-center gap-1.5 !border-emerald-500 !text-emerald-600">
              <Flag className="w-4 h-4" /> Mark completed
            </button>
          )}
          {isCompleted && isOwner && (
            <button onClick={reopenEvent} className="btn-outline text-sm flex items-center gap-1.5 !border-brand !text-brand">
              <Unlock className="w-4 h-4" /> Reopen event
            </button>
          )}
        {isOwner && !isCompleted && (
            <Link to={`/edit-event/${id}`} className="btn-outline text-sm flex items-center gap-1.5">
              <Pencil className="w-4 h-4" /> Edit event
            </Link>
          )}
          {isOwner && (
            <button onClick={() => setShowInvite(s => !s)} className="btn-outline text-sm flex items-center gap-1.5">
              <UserPlus className="w-4 h-4" /> Invite co-manager
            </button>
          )}  
        </div>
      </div>

      {/* Invite co-manager */}
      {showInvite && isOwner && (
        <div className="mt-4 card-flat">
          <div className="flex gap-2 items-start flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">@</span>
              <input
                className="input-base pl-7"
                placeholder="Search username…"
                value={inviteUsername}
                onChange={e => handleUsernameChange(e.target.value)}
                autoComplete="off"
              />
              {/* Autocomplete dropdown */}
              {userSuggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-popover border border-border rounded-xl shadow-xl z-50 overflow-hidden">
                  {userSuggestions.map(u => (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => selectUser(u.username)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-brand-tint transition text-left"
                    >
                      {u.avatar
                        ? <img src={u.avatar} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                        : <div className="w-8 h-8 rounded-full bg-brand-tint grid place-items-center text-xs font-bold text-brand flex-shrink-0">{(u.name || "?")[0]}</div>
                      }
                      <div>
                        <p className="text-sm font-medium text-foreground">@{u.username}</p>
                        <p className="text-xs text-muted-foreground">{u.name}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {searchingUsers && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-popover border border-border rounded-xl shadow-xl z-50 p-3 text-center text-xs text-muted-foreground">
                  Searching…
                </div>
              )}
            </div>
            <button onClick={sendInvite} disabled={inviting || !inviteUsername.trim()} className="btn-primary text-sm">
              {inviting ? "Sending…" : "Send invite"}
            </button>
            <button onClick={() => { setShowInvite(false); setUserSuggestions([]); }} className="btn-outline text-sm">Cancel</button>
          </div>
        </div>
      )}

      {/* Stats — always visible even after completion */}
      <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard k="Job roles" v={event.jobs?.length || 0} />
        <StatCard k="Total slots" v={totalSlots} />
        <StatCard k="Confirmed" v={confirmedSlots} color="text-emerald-600" />
        <StatCard k="Remaining" v={Math.max(0, totalSlots - confirmedSlots)} color="text-brand" />
      </div>

      {/* Quick actions — hide add role when completed */}
      <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3">
        {!isCompleted && (
          <Link to={`/manage-event/${id}/add-role`} className="card-flat flex items-center gap-3 hover:border-brand transition group">
            <div className="w-10 h-10 rounded-full bg-brand-tint grid place-items-center flex-shrink-0">
              <Plus className="w-5 h-5 text-brand" />
            </div>
            <div>
              <p className="font-medium text-foreground text-sm">Add job role</p>
              <p className="text-xs text-muted-foreground">Post a role under this event</p>
            </div>
          </Link>
        )}
        <Link to={`/manage-event/${id}/expenses`} className="card-flat flex items-center gap-3 hover:border-brand transition">
          <div className="w-10 h-10 rounded-full bg-brand-tint grid place-items-center flex-shrink-0">
            <Receipt className="w-5 h-5 text-brand" />
          </div>
          <div>
            <p className="font-medium text-foreground text-sm">Expenses</p>
            <p className="text-xs text-muted-foreground">Track event expenses</p>
          </div>
        </Link>
        <Link to={`/events/${id}`} className="card-flat flex items-center gap-3 hover:border-brand transition">
          <div className="w-10 h-10 rounded-full bg-brand-tint grid place-items-center flex-shrink-0">
            <ExternalLink className="w-5 h-5 text-brand" />
          </div>
          <div>
            <p className="font-medium text-foreground text-sm">Public page</p>
            <p className="text-xs text-muted-foreground">View as workers see it</p>
          </div>
        </Link>
      </div>

      {/* Job roles list */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl tracking-tight">
            Job roles ({event.jobs?.length || 0})
          </h2>
          {!isCompleted && (
            <Link to={`/manage-event/${id}/add-role`} className="btn-primary text-sm !py-2">+ Add role</Link>
          )}
        </div>

        {!event.jobs?.length ? (
          <div className="card-flat text-center py-12">
            <div className="w-12 h-12 rounded-full bg-brand-tint grid place-items-center mx-auto mb-3">
              <Plus className="w-6 h-6 text-brand" />
            </div>
            <p className="font-display text-xl">No roles yet</p>
            <p className="text-sm text-muted-foreground mt-1">Add job roles to start accepting applications</p>
            {!isCompleted && (
              <Link to={`/manage-event/${id}/add-role`} className="btn-primary mt-4 inline-flex">+ Add first role</Link>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {event.jobs.map(j => (
              <Link
                key={j.id}
                to={`/manage/${j.id}?from=event&event_id=${id}`}
                className="card-flat block hover:border-brand transition cursor-pointer"
              >
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <p className="font-medium text-foreground">{j.title}</p>
                    <p className="text-xs text-muted-foreground">{j.specialization}</p>
                  </div>
                  <div className="flex items-center gap-3 text-xs flex-wrap">
                    <span className="text-emerald-600 font-medium flex items-center gap-1">
                      <Users className="w-3.5 h-3.5" />
                      {j.confirmed_count || 0}/{j.total_slots} confirmed
                    </span>
                    {j.remaining_slots === 0 && j.status !== "completed" && (
                      <span className="text-xs text-amber-600 font-medium">Full</span>
                    )}
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      j.status === "completed" ? "bg-emerald-100 text-emerald-700" :
                      j.status === "closed" ? "bg-amber-100 text-amber-700" :
                      "bg-blue-100 text-blue-700"
                    }`}>{j.status}</span>
                    <span className="text-brand font-medium">Manage →</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Co-managers */}
      {(isOwner || isCoManager) && event.co_managers?.length > 0 && (
        <div className="mt-8">
          <h2 className="font-display text-xl mb-3">Co-managers ({event.co_managers.length})</h2>
          <div className="card-flat">
            <p className="text-sm text-muted-foreground">Co-managers can manage attendance, expenses and broadcast messages.</p>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ k, v, color = "text-foreground" }) {
  return (
    <div className="border border-border p-4 rounded-sm bg-background">
      <p className={`font-display text-3xl ${color}`}>{v}</p>
      <p className="text-xs text-muted-foreground mt-1">{k}</p>
    </div>
  );
}
